import React, { useState } from 'react';
import { Mail, Phone, MapPin, Calendar, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { useLanguage } from '../context/LanguageContext';
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
export function Contact() {
  const {
    t
  } = useLanguage();
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    try {
      const response = await fetch('https://formspree.io/f/xojwngpj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setFormStatus('success');
        setFormData({
          name: '',
          email: '',
          message: ''
        });
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.id]: e.target.value
    }));
  };
  return <section id="contact" className="py-24 bg-slate-950 relative overflow-hidden" data-id="element-30">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-cyan-900/5 to-transparent pointer-events-none" data-id="element-31" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-32">
        <div className="text-center mb-16" data-id="element-33">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-34">
            {t.contact.title}
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-35">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 items-start" data-id="element-36">
          <div className="lg:col-span-2 space-y-8" data-id="element-37">
            <div className="bg-slate-900 p-8 rounded-2xl border border-white/5" data-id="element-38">
              <h3 className="text-xl font-bold text-white mb-6" data-id="element-39">
                {t.contact.info.title}
              </h3>
              <div className="space-y-6" data-id="element-40">
                <div className="flex items-center gap-4 text-slate-300" data-id="element-41">
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-cyan-400 flex-shrink-0" data-id="element-42">
                    <Mail size={20} data-id="element-43" />
                  </div>
                  <span data-id="element-44">quickdesigns2026@gmail.com</span>
                </div>
                <div className="flex items-center gap-4 text-slate-300" data-id="element-45">
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-cyan-400 flex-shrink-0" data-id="element-46">
                    <Phone size={20} data-id="element-47" />
                  </div>
                  <span data-id="element-48">+48 698 945 602</span>
                </div>
                <div className="flex items-center gap-4 text-slate-300" data-id="element-49">
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-cyan-400 flex-shrink-0" data-id="element-50">
                    <MapPin size={20} data-id="element-51" />
                  </div>
                  <span data-id="element-52">Gdynia, PL</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 p-8 rounded-2xl border border-white/5" data-id="element-53">
              <h3 className="text-xl font-bold text-white mb-6" data-id="element-54">
                {t.contact.form.title}
              </h3>

              {formStatus === 'success' ? <div className="flex flex-col items-center justify-center py-8 text-center" data-id="element-55">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4" data-id="element-56">
                    <CheckCircle className="text-emerald-400" size={32} data-id="element-57" />
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-2" data-id="element-58">
                    {t.contact.form.success.title}
                  </h4>
                  <p className="text-slate-400 mb-6" data-id="element-59">
                    {t.contact.form.success.subtitle}
                  </p>
                  <Button variant="outline" onClick={() => setFormStatus('idle')} data-id="element-60">
                    {t.contact.form.success.another}
                  </Button>
                </div> : <form className="space-y-4" onSubmit={handleSubmit} data-id="element-61">
                  {formStatus === 'error' && <div className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400" data-id="element-62">
                      <AlertCircle size={20} data-id="element-63" />
                      <span className="text-sm" data-id="element-64">{t.contact.form.error}</span>
                    </div>}
                  <div data-id="element-65">
                    <label htmlFor="name" className="block text-sm font-medium text-slate-400 mb-1" data-id="element-66">
                      {t.contact.form.name}
                    </label>
                    <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors" placeholder={t.contact.form.namePlaceholder} data-id="element-67" />
                  </div>
                  <div data-id="element-68">
                    <label htmlFor="email" className="block text-sm font-medium text-slate-400 mb-1" data-id="element-69">
                      {t.contact.form.email}
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors" placeholder={t.contact.form.emailPlaceholder} data-id="element-70" />
                  </div>
                  <div data-id="element-71">
                    <label htmlFor="message" className="block text-sm font-medium text-slate-400 mb-1" data-id="element-72">
                      {t.contact.form.message}
                    </label>
                    <textarea id="message" name="message" required rows={4} value={formData.message} onChange={handleChange} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors resize-none" placeholder={t.contact.form.messagePlaceholder} data-id="element-73" />
                  </div>
                  <Button className="w-full" type="submit" isLoading={formStatus === 'submitting'} data-id="element-74">
                    {formStatus === 'submitting' ? t.contact.form.submitting : <>
                        {t.contact.form.submit}
                        <Send className="ml-2" size={18} data-id="element-75" />
                      </>}
                  </Button>
                </form>}
            </div>
          </div>

          <div className="lg:col-span-3 h-full min-h-[600px] bg-slate-900 rounded-2xl border border-white/5 overflow-hidden flex flex-col" data-id="element-76">
            <div className="p-6 border-b border-white/5 bg-slate-900 flex items-center gap-3" data-id="element-77">
              <Calendar className="text-cyan-400" size={20} data-id="element-78" />
              <h3 className="text-lg font-semibold text-white" data-id="element-79">
                {t.contact.booking.title}
              </h3>
            </div>
            <div className="flex-1 w-full bg-white" data-id="element-80">
              <iframe src="https://cal.com/quick-designs/30min" width="100%" height="100%" frameBorder="0" title="Book a call with Quick Designs" className="w-full h-full min-h-[600px]" data-id="element-81"></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>;
}